import { Formik } from "formik";
import { object } from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FunnelButton } from "../../FunnelButton";
import { FunnelBackButton } from "../../FunnelBackButton";
import {
  GoogleAddressAutocomplete,
  useFormikAddressCheckOnBeforeSubmit,
} from "@portal/app/_components/ui-elements/GoogleAddressAutocomplete";
import { logger } from "@portal/app/_common/lib/logger";
import { useFormFunnelTracking } from "../../hooks/useFormFunnelTracking";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { QuestionDescription } from "../../QuestionDescription";
import { AddressStepProps } from "./types";
import { useHostedStepAction } from "./useHostedStepAction";
import { useFormFunnelHeadingLevel } from "../../FormFunnelHeadingContext";

export const AddressStep = ({
  viewerCustomer,
  customerDataRef,
  updateCustomerDetails,
  refetchViewer,
  onNext,
  onBack,
  description,
}: AddressStepProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const { onBeforeSubmit } = useFormikAddressCheckOnBeforeSubmit({ fieldNamespace: "address" });
  const { trackEvent } = useFormFunnelTracking();
  const action = useHostedStepAction();

  const descriptionText = description?.content ? (
    <Box sx={{ my: 1 }}>
      <QuestionDescription content={description.content} testId="hosted-address-step-description" />
    </Box>
  ) : null;

  return (
    <Formik
      initialValues={{
        address: {
          street: viewerCustomer?.detailsMap?.address?.street || "",
          street2: viewerCustomer?.detailsMap?.address?.street2 || "",
          city: viewerCustomer?.detailsMap?.address?.city || "",
          state: viewerCustomer?.detailsMap?.address?.state || "",
          zip: viewerCustomer?.detailsMap?.address?.zip || "",
        },
      }}
      validationSchema={object({
        address: object({
          street: yupRules.required.street,
          street2: yupRules.optional.street2,
          city: yupRules.required.city,
          state: yupRules.required.state,
          zip: yupRules.required.zip,
        }),
      })}
      onSubmit={async (values, formikHelpers) => {
        const generation = action.begin();
        if (generation === undefined) return;
        try {
          const isValidAddress = await onBeforeSubmit(values, formikHelpers);
          if (!isValidAddress || !action.isCurrent(generation)) return;
          // Save address to customer data for final submission
          customerDataRef.current.address = {
            street: values.address.street,
            street2: values.address.street2 || undefined,
            city: values.address.city,
            state: values.address.state,
            zip: values.address.zip,
          };

          await updateCustomerDetails({
            variables: {
              data: {
                address: {
                  street: values.address.street,
                  street2: values.address.street2,
                  city: values.address.city,
                  state: values.address.state,
                  zip: values.address.zip,
                },
              },
            },
          });
          await refetchViewer();
          if (!action.complete(generation)) return;
          trackEvent("Form Funnel - Hosted Step Completed", { stepType: "address" });
          onNext();
        } catch (e) {
          logger.error(e);
        } finally {
          action.finish(generation);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, isValid }) => (
        <form onSubmit={handleSubmit} data-testid="hosted-address-step">
          {onBack && (
            <FunnelBackButton appearance="text" onClick={onBack} sx={{ mb: 1 }}>
              Back
            </FunnelBackButton>
          )}
          <Typography
            component={headingLevel}
            sx={{
              color: "text.primary",
              fontSize: "1.5rem",
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
              mb: 3,
            }}
          >
            What is your address?
          </Typography>
          {description?.placement === "above" && descriptionText}
          <GoogleAddressAutocomplete
            fieldNamespace="address"
            requireStreetNumber
            appearance="hosted"
          />
          {description?.placement !== "above" && descriptionText}
          <FunnelButton
            type="submit"
            disabled={!isValid || isSubmitting}
            sx={{ maxWidth: "none", minHeight: 56, mt: 3, fontSize: "1.1rem" }}
          >
            Continue
          </FunnelButton>
        </form>
      )}
    </Formik>
  );
};
